import React from 'react';
import { Box, Text, HStack, VStack, Avatar, Badge } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task, User, Comment } from '../types';
import { useAuth } from '../context/AuthContext';
import { resolveLogoUrl } from '../utils/logoUrl';
import { htmlToPlainText, isEmptyHtml } from '../utils/sanitizeHtml';

function getLatestComment(task: Task): Comment | null {
  if (!task.comments?.length) return null;
  return [...task.comments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0];
}

function commentPreviewText(comment: Comment): string {
  if (!isEmptyHtml(comment.text)) {
    const plain = htmlToPlainText(comment.text);
    if (plain) return plain;
  }
  if ((comment.attachments?.length ?? 0) > 0) {
    const name = comment.attachments![0].originalName;
    const extra = comment.attachments!.length > 1 ? ` (+${comment.attachments!.length - 1})` : '';
    return `📎 ${name}${extra}`;
  }
  return '';
}

const PRIORITY_META: Record<Task['priority'], { label: string; colorScheme: string }> = {
  high: { label: 'High', colorScheme: 'red' },
  medium: { label: 'Medium', colorScheme: 'orange' },
  low: { label: 'Low', colorScheme: 'green' },
};

interface TaskCardProps {
  task: Task;
  statusLabel?: string;
  onClick: () => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, statusLabel, onClick }) => {
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin';
  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const userId = user?.id;

  const participants: User[] = [];
  if (Array.isArray(task.participants) && task.participants.length > 0) {
    participants.push(
      ...task.participants.filter(
        (p) => p && (p.id || (p as any)._id)
      )
    );
  }
  if (task.assignedTo && !participants.some((p) => (p.id || p._id) === (task.assignedTo as any).id || (task.assignedTo as any)._id)) {
    participants.push(task.assignedTo);
  }
  if (
    task.createdBy &&
    !participants.some(
      (p) => (p.id || p._id) === (task.createdBy?.id || (task.createdBy as any)?._id)
    )
  ) {
    participants.push(task.createdBy);
  }

  const canEdit =
    !!isAdmin ||
    !!isMasterAdmin ||
    !!(
      userId &&
      participants.some(
        (p) => (p.id || p._id)?.toString() === userId
      )
    );
  const displayParticipants = participants.slice(0, 3);
  const extraCount = participants.length > 3 ? participants.length - 3 : 0;
  const latestComment = getLatestComment(task);
  const latestCommentPreview = latestComment ? commentPreviewText(latestComment) : '';
  const priorityMeta = PRIORITY_META[task.priority] ?? PRIORITY_META.medium;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task._id,
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getInitials = (user: User) => (user?.name?.split(' ').map((n) => n[0]).join('').toUpperCase() || '?').slice(0, 2);
  const getUserAvatarSrc = (u: User): string | null => {
    const direct = (u as any)?.avatarUrl;
    if (direct) return resolveLogoUrl(direct);
    const stored = (u as any)?.avatar?.storedFilename;
    if (stored) return resolveLogoUrl(`/api/user-avatars/${encodeURIComponent(String(stored))}`);
    return null;
  };

  // Handle click - only trigger if not dragging
  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger onClick if we're in the middle of a drag
    if (!isDragging) {
      onClick();
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      bg="white"
      p={{ base: 3, md: 5 }}
      borderRadius="lg"
      borderWidth={1}
      borderColor="gray.200"
      position="relative"
      _hover={{ 
        boxShadow: 'lg',
        transform: 'translateY(-2px)',
        borderColor: 'blue.300',
      }}
      boxShadow="sm"
      transition="all 0.2s"
    >
      {canEdit && (
      <Box
        {...listeners}
        {...attributes}
        position="absolute"
        left={0}
        top={0}
        bottom={0}
        width="20px"
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        _hover={{ bg: 'gray.100' }}
        borderRadius="lg 0 0 lg"
      >
        <Box
          width="4px"
          height="40px"
          bg="gray.300"
          borderRadius="2px"
          _hover={{ bg: 'blue.400' }}
        />
      </Box>
      )}
      {/* Clickable content area */}
      <Box
        onClick={handleClick}
        cursor="pointer"
        pl={canEdit ? 6 : 5}
      >
        <HStack justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
          <Text fontWeight="semibold" color="gray.800" fontSize={{ base: 'sm', md: 'md' }} flex={1}>
            {task.title}
          </Text>
          <Badge
            size="sm"
            variant="subtle"
            colorScheme={priorityMeta.colorScheme}
            flexShrink={0}
          >
            {priorityMeta.label}
          </Badge>
        </HStack>
        <Box mb={3}>
          <HStack justifyContent="space-between" mb={1}>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">Progress</Text>
            <Text fontSize="xs" color="blue.600" fontWeight="semibold">{task.completionPercent ?? 0}%</Text>
          </HStack>
          <Box h="4px" borderRadius="full" bg="gray.200" overflow="hidden">
            <Box h="100%" w={`${task.completionPercent ?? 0}%`} bg="blue.500" />
          </Box>
        </Box>
        <VStack align="stretch" gap={2}>
          {statusLabel && (
            <HStack gap={1} fontSize="xs" color="gray.600">
              <Text fontWeight="medium">Status:</Text>
              <Badge size="sm" variant="subtle" colorScheme="gray">{statusLabel}</Badge>
            </HStack>
          )}
          {(task.dependsOn?.length ?? 0) > 0 && (
            <HStack gap={1} fontSize="xs" color="gray.600">
              <Text fontWeight="medium">Depends on:</Text>
              <Text>{task.dependsOn!.length} {task.dependsOn!.length === 1 ? 'task' : 'tasks'}</Text>
            </HStack>
          )}
          <HStack gap={2} alignItems="center" fontSize="xs" color="gray.600">
            <Text fontWeight="medium">Participants:</Text>
            <HStack gap={1}>
              {displayParticipants.length > 0 ? (
                <>
                  {displayParticipants.map((u) => {
                    const key = u.id || (u as any)._id;
                    const avatarSrc = getUserAvatarSrc(u);
                    return (
                      <Box key={key} title={u.name || undefined}>
                        {avatarSrc ? (
                          <img
                            src={avatarSrc}
                            alt={u.name || 'User avatar'}
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '9999px',
                              objectFit: 'cover',
                              border: '1px solid #e5e7eb',
                            }}
                          />
                        ) : (
                          <Avatar.Root size="xs">
                            <Avatar.Fallback bg="gray.200" color="black" fontWeight="bold" fontSize="2xs">
                              {getInitials(u)}
                            </Avatar.Fallback>
                          </Avatar.Root>
                        )}
                      </Box>
                    );
                  })}
                  {extraCount > 0 && (
                    <Badge
                      as="span"
                      fontSize="xs"
                      bg="gray.200"
                      color="black"
                      borderRadius="full"
                      px={2}
                      title={participants
                        .slice(3)
                        .map((u) => u.name)
                        .filter(Boolean)
                        .join(', ') || undefined}
                    >
                      +{extraCount}
                    </Badge>
                  )}
                </>
              ) : (
                <Text fontStyle="italic">—</Text>
              )}
            </HStack>
          </HStack>
          {latestComment && latestCommentPreview && (
            <Box
              mt={1}
              p={2}
              bg="gray.50"
              borderRadius="md"
              borderWidth={1}
              borderColor="gray.100"
            >
              <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={0.5}>
                Last comment · {latestComment.createdBy?.name || 'Unknown'}
              </Text>
              <Text
                fontSize="xs"
                color="gray.700"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {latestCommentPreview}
              </Text>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default TaskCard;
